import { WalletAddressFormSchema } from 'BreetComponents';
import {
	alwaysRefetchApiQueryOptions,
	filterDisabledToken,
	type IDropDownItem,
	type IUseWalletAddressProviderApi,
	type IWalletAddress,
	type IWalletInfoOptionProps,
	showToast,
	type TokenType,
} from 'BreetHelpers';
import { selectAuthSettings, useAppSelector, useGetWalletInfoQuery } from 'BreetRedux';
import { useCallback, useMemo } from 'react';

import { useFormData } from './useFormData';

export const useWalletAddressProviderApi = (): IUseWalletAddressProviderApi => {
	const authSettings = useAppSelector(selectAuthSettings);

	const { data: walletInfoData } = useGetWalletInfoQuery(undefined, alwaysRefetchApiQueryOptions);

	const walletInfoOptions: IWalletInfoOptionProps[] = useMemo(() => {
		if (!walletInfoData?.data) return [];

		return Object.entries(walletInfoData.data).map(([key, value]) => ({
			...value,
			token: key as TokenType,
		}));
	}, [walletInfoData?.data]);

	const tokenOptions: IDropDownItem[] = useMemo(
		() =>
			walletInfoOptions.filter(filterDisabledToken(authSettings?.usdWithdrawalMeta)).map((option) => ({
				value: option.token,
				icon: option.avatar,
				code: option.token,
				name: option.token,
				id: option.token,
			})),
		[authSettings?.usdWithdrawalMeta, walletInfoOptions]
	);

	const sendCryptoFormData = useFormData(
		WalletAddressFormSchema,
		{
			token: '',
			network: '',
			receiverAddress: '',
			walletName: '',
			amount: '0',
		},
		{
			mode: 'onChange',
		}
	);

	const resetWalletAddressProps = useCallback(() => {
		sendCryptoFormData.hookFormProps.reset({
			...sendCryptoFormData.hookFormProps.watch(),
			token: '',
			network: '',
			receiverAddress: '',
			walletName: '',
		});
	}, [sendCryptoFormData]);

	const handleSelectAddress = useCallback(
		async (item: IWalletAddress) => {
			try {
				const token = tokenOptions.find((i) => i.name === item.token);

				if (!token) {
					showToast('error', 'This coin is currently unavailable. Please try again later or contact support.');
					return;
				}

				const usdWithdrawalMeta = authSettings?.usdWithdrawalMeta;

				if (!usdWithdrawalMeta) return [];

				const netWork = usdWithdrawalMeta.vip.networks[item.network];
				const tokenNetworks = usdWithdrawalMeta.vip.tokens[item.token].networks;
				const isEnabledForToken = item.network in tokenNetworks ? tokenNetworks[item.network].enabled : true;

				if (!netWork.enabled || !isEnabledForToken) {
					showToast('error', 'This network is currently unavailable. Please try again later or contact support.');
					return;
				}

				await sendCryptoFormData.setValueAndValidate('token', item.token);
				await sendCryptoFormData.setValueAndValidate('network', item.network);
				await sendCryptoFormData.setValueAndValidate('receiverAddress', item.address);
				await sendCryptoFormData.setValueAndValidate('walletName', item.label);
			} catch (error) {
				console.error(error);
			}
		},
		[tokenOptions, authSettings?.usdWithdrawalMeta, sendCryptoFormData]
	);

	const providerValue = useMemo(
		() => ({
			formDataContextProps: sendCryptoFormData,
			tokenOptions,
			walletInfoOptions,
			walletInfoData,
			authSettings,
			resetWalletAddressProps,
			handleSelectAddress,
		}),
		[
			walletInfoData,
			sendCryptoFormData,
			tokenOptions,
			walletInfoOptions,
			authSettings,
			resetWalletAddressProps,
			handleSelectAddress,
		]
	);

	return providerValue;
};
